<template>
  <div v-if="isLoading" class="loading-container">
    <p>Loading...</p>
  </div>
  <div
    v-else
    class="gant-together-container"
    :style="{ '--custom-color': customColor }"
  >
    <div class="box mt-5">
      <div class="has-text-centered is-block my-5">
        <img :src="logoUrl" alt="Event Logo" width="280" />
      </div>

      <!-- Select event screen -->
      <div class="select-event-container" v-if="stage === 'start'">
        <h1 class="is-size-4 has-text-centered">
          Book travel for your {{ accountName }} event
        </h1>
        <h2 class="is-size-5 has-text-centered has-text-grey mb-5">
          With TripCircuit from Gant Travel
        </h2>

        <b-notification type="is-info is-light">
          Enter the contact details for the person attending the event below. We
          will send a verification code to the email and mobile phone you enter.
        </b-notification>

        <form>
          <b-notification v-if="validationError" type="is-danger is-light">
            {{ validationError }}
          </b-notification>

          <b-field
            v-if="showEventSelector"
            label="Select the event"
            :label-position="labelPosition"
            :class="fieldClass"
          >
            <b-select v-model="eventId" expanded required>
              <option
                v-for="option in events"
                :value="option.id"
                :key="option.id"
              >
                {{ option.displayName }}
              </option>
            </b-select>
          </b-field>

          <b-field
            v-if="!showEventSelector && event"
            label="Event"
            :label-position="labelPosition"
            :class="fieldClass"
          >
            <b-input :value="event.displayName" disabled> </b-input>
          </b-field>

          <b-field
            label="Event attendee's email"
            :label-position="labelPosition"
            :class="fieldClass"
          >
            <b-input type="email" v-model="email" required> </b-input>
          </b-field>

          <b-field
            label="Event attendee's mobile phone (optional)"
            :label-position="labelPosition"
            :class="fieldClass"
            :message="mobilePhoneValidationStateMessage"
            :type="mobilePhoneValidationStateClass"
          >
            <vue-tel-input
              v-model="mobilePhone"
              defaultCountry="US"
              mode="international"
              @validate="mobilePhoneValidate"
            ></vue-tel-input>
          </b-field>

          <b-button @click="beginVerifyDetails" type="is-primary" class="mt-4"
            >Save and Send Verification Code</b-button
          >
        </form>
      </div>
      <!-- End Select event screen -->

      <!-- Verify screen -->
      <div class="verify-email-container" v-if="stage == 'verify'">
        <h1 class="is-size-4 has-text-centered">
          Enter Your Verification Code
        </h1>

        <h2 class="is-size-5 has-text-centered has-text-grey mb-3" v-if="event">
          {{ event.name }}
        </h2>

        <b-notification
          v-if="email != null || mobilePhone != null"
          class="is-info is-light mt-3"
        >
          <h4 class="is-size-5">Your verification code has been sent!</h4>
          <p>Please enter it below to continue.</p>

          <div class="content">
            <ul>
              <li v-if="email">
                Email sent to <strong>{{ email }}</strong>
              </li>
              <li v-if="mobilePhone">
                SMS text message sent to <strong>{{ mobilePhone }}</strong>
              </li>
            </ul>
          </div>

          <p>
            <strong>Didn't receive your code?</strong> It may take a minute or
            two to arrive.
          </p>
          <p>
            If you have a typo and want to try again
            <a @click="changeStage('start')">go back to previous step</a>.
          </p>
        </b-notification>

        <b-notification v-if="verificationError" type="is-danger is-light">
          {{ verificationError }}
        </b-notification>

        <b-field
          label="Verification code"
          :label-position="labelPosition"
          :class="fieldClass"
          class="mt-6"
        >
          <b-input type="" v-model="enteredVerificationCode"> </b-input>
        </b-field>

        <b-button @click="checkVerificationCode" type="is-primary" class="mt-4"
          >Confirm Verification Code</b-button
        >
      </div>
      <!-- End Verify screen -->

      <!-- Loading screen -->
      <div class="loading-container" v-if="stage == 'loading'">
        loading
      </div>
      <!-- End Loading screen -->

      <!-- Success screen -->
      <!-- Success screen -->
      <div class="success-container" v-if="stage === 'success'">
        <b-notification v-if="attendeeError" type="is-danger is-light">
          {{ attendeeError }}
        </b-notification>

        <b-notification type="is-success is-light" v-else>
          Thank you for verifying your contact details. You will now be logged
          in to begin booking your trip.
        </b-notification>
      </div>
      <!-- End Success screen -->

      <!-- End Success screen -->
    </div>

    <div class="has-text-centered">
      <strong>TripCircuit</strong> by
      <a href="https://www.ganttravel.com/" target="_blank">Gant Travel</a>
    </div>
  </div>
</template>
<script>
import { VueTelInput } from "vue-tel-input";
var moment = require("moment");

export default {
  name: "GantTogetherMeetingsAuth",
  components: {
    VueTelInput,
  },
  data: function() {
    return {
      isLoading: true, // Loading state
      labelPosition: "on-border",
      fieldClass: "mb-5",
      stage: "start",
      accountId: null,
      events: [],
      eventId: null,
      showEventSelector: true,
      mobilePhone: null,
      email: null,
      validationError: null,
      logoUrl: "/images/TC wired for Gant Travel.png", // Default logo URL
      customColor: "#a4393a", // Gant Red
      accountName: "Gant Together",
      attendeeError: null,
    };
  },
  methods: {
    fetchAccountAndMeetings() {
      const slug = this.$route.params.slug; // Get dynamic slug from the URL

      if (!this.isValidSlug(slug)) {
        console.error("Malformed slug detected:", slug);
        this.$router.push("/"); // Redirect to home if slug is malformed
        return;
      }

      const body = { slug };

      this.$app
        .huddleLabsServerCall("meetings/get-account-and-meetings", body)
        .then((result) => {
          if (result) {
            // Assign account-related data
            this.accountId = result.accountId;
            this.accountName = result.accountName;
            this.meetingLocation = result.meetingLocation;
            this.logoUrl = result.logoUrl || this.logoUrl;
            // Validate custom color or use the default
            this.customColor = this.isValidHexColor(result.customColor)
              ? result.customColor
              : "#a4393a"; // Default Gant Red
            // Process meetings for display
            const events = [];
            for (let meeting of result.meetings) {
              let displayName = meeting.name;

              // Append location if available
              if (meeting.meetingLocation) {
                displayName += `, ${meeting.meetingLocation}`;
              }

              // Append date range if available
              if (meeting.meetingStartDate) {
                const startDate = moment(meeting.meetingStartDate).format(
                  "MMM D, YY"
                );
                displayName += ` (${startDate}`;
              }
              if (meeting.meetingEndDate) {
                const endDate = moment(meeting.meetingEndDate).format(
                  "MMM D, YY"
                );
                displayName += ` - ${endDate})`;
              }

              const event = {
                id: meeting.id,
                name: meeting.name,
                displayName: displayName,
                status: meeting.status,
              };

              // Automatically select the meeting based on registration code
              if (
                this.$route.query.code &&
                this.$route.query.code == meeting.meetingRegistrationCode
              ) {
                this.eventId = meeting.id;
                this.showEventSelector = false;
              }

              events.push(event);
            }

            this.events = events; // Assign processed events
          } else {
            this.$router.push("/"); // Redirect to home if no account data
          }
        })
        .catch(() => {
          this.$router.push("/"); // Redirect to home on API error
        })
        .finally(() => {
          this.isLoading = false; // Stop loading
        });
    },
    changeStage(stage) {
      this.stage = stage;
    },
    beginVerifyDetails() {
      if (!this.performValidation()) return;
      // Send email or mobile phone verification code
      if (this.email || this.mobilePhone) {
        this.sendVerificationCode();
      }

      // Set next stage based on type of verification used
      this.stage = "verify";
    },
    performValidation() {
      if (!this.eventId || !this.email) {
        this.validationError =
          "Please select an event and enter an email address.";
        return false;
      }
      this.validationError = null;
      return true;
    },
    sendVerificationCode() {
      if (!this.email && !this.mobilePhone) return;

      var body = {
        email: this.email,
        mobilePhone: this.mobilePhoneFormatted,
      };

      this.$app
        .huddleLabsServerCall("actions/send-verification-code", body)
        .then((result) => {
          this.verificationKey = result.key;
        });
    },

    checkVerificationCode() {
      var body = {
        key: this.verificationKey,
        enteredCode: this.enteredVerificationCode,
      };

      this.$app
        .huddleLabsServerCall("actions/verify-code", body)
        .then((result) => {
          // Success
          if (result.status == "confirmed") {
            this.handleSuccessfulVerification();
          }

          // Fail
          this.handledFailedVerification();
        });
    },
    handleSuccessfulVerification() {
      // Codes do match
      this.stage = "success";
      this.verificationError = null;
      this.findOrCreateUser();
    },
    handledFailedVerification() {
      this.verificationError =
        'The verification code you entered "' +
        this.enteredVerificationCode +
        '" does not match the verification code that was sent.';
    },
    findOrCreateUser() {
      let meetingAttendee = null;

      const body = {
        accountId: this.accountId,
        meetingId: this.eventId,
        email: this.email,
        mobilePhone: this.mobilePhoneFormatted,
      };

      this.$app
        .huddleLabsServerCall("meetings/attendee/retrieve", body)
        .then((result) => {
          console.log("API response:", result); // Log response for debugging
          if (result.success === false) {
            // Handle backend error
            this.attendeeError =
              result.message || "An unexpected error occurred.";
            console.log("Error message set:", this.attendeeError); // Debug error message
            this.stage = "success"; // Show success container for errors
          } else if (result.data) {
            // Handle success
            this.attendeeError = null; // Clear any previous error
            this.redirectToLogin(result.data);
          }
        })
        .catch((error) => {
          // Handle network or unexpected errors
          console.error("API error:", error.message); // Debugging network errors
          this.attendeeError = error.message || "An unexpected error occurred.";
          this.stage = "success"; // Show success container for errors
        });

      return meetingAttendee;
    },

    redirectToLogin(attendeeData) {
      // console.log(attendeeData)

      let params = {
        email: attendeeData.email,
        access_code: attendeeData.attendeeAccessCode,
        meeting_code: attendeeData.meetingCode,
      };

      let paramsString = new URLSearchParams(params).toString();
      let urlPath = "/meetings?" + paramsString;

      // console.log('Redirect to: ' + urlPath)

      this.$router.push({ path: urlPath });
    },
    mobilePhoneValidate(phoneObject) {
      this.validMobilePhone = phoneObject.valid;
    },

    isValidHexColor(color) {
      const hexColorRegex = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i;
      return hexColorRegex.test(color);
    },
    isValidSlug(slug) {
      const slugRegex = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
      return slugRegex.test(slug);
    },
  },
  mounted() {
    this.fetchAccountAndMeetings(); // Fetch account and meetings on load
  },
};
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"></style>
<style scoped>
.gant-together-container {
  width: 500px;
  max-width: 96%;
  margin: 0 auto;
}

.loading-container {
  text-align: center;
  padding: 50px;
  font-size: 1.2em;
  color: #999;
}

button.is-primary {
  background-color: var(--custom-color) !important;
  border-color: var(--custom-color) !important;
}
</style>
